import HCaptcha from '@hcaptcha/react-hcaptcha'
import { FC, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useAccount } from 'wagmi'
import useProfileAPI from '../../../__legacy__/api/ProfileAPI'
import { useAuth } from '../../hooks/useAuth'
import usePrevious from '../../hooks/usePrevious'
import { segmentIdentifyUser, segmentSignUp } from '../../lib/segment'
import MagicConnector from '../../lib/wagmi/MagicConnector'
import { useCreateUser } from '../../queries/users'
import AuthStore from '../../stores/AuthStore'
import Button from '../core/Button/Button'
import { FieldErrorLabel } from '../core/FieldErrorLabel'
import { FieldLabel } from '../core/FieldLabel'
import { Flex } from '../core/Flex'
import { HoneyPotFields, IHoneyPotFields } from '../core/HoneyPotFields'
import Spacer from '../core/Spacer/Spacer'
import Text from '../core/Text/Text'
import { TextInput } from '../core/TextInput'
import { Spinner } from '../platform/explore/common/utils'
import { AuthFlowPosition } from './AuthFlowModal'
import { SelectProfilePicture } from './SelectProfilePicture'

export const emailRegex = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
)

export type CreateAccountForm = {
  displayName: string
  email: string
  walletAddress: string
  hcaptchaToken: string
} & IHoneyPotFields

interface Props {
  setFlowPosition: React.Dispatch<React.SetStateAction<AuthFlowPosition>>
  emailOverload?: string
}

const CreateAccount: FC<Props> = ({ setFlowPosition, emailOverload }) => {
  const { setIsLoggedIn } = AuthStore
  const createUser = useCreateUser()
  const { logout, silentLogin, getWalletProviderCookie } = useAuth()
  const { address: walletAddress, connector } = useAccount()
  const { getIsValidDisplayName } = useProfileAPI()
  const [profilePicture, setProfilePicture] = useState<File | null>(null)
  const [hcaptchaToken, setHcaptchaToken] = useState<string>(null)
  const previousWalletAddress = usePrevious(walletAddress)

  emailOverload =
    emailOverload ||
    (connector?.id === 'magic' ? (connector as MagicConnector).email : null) ||
    null

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue
  } = useForm<CreateAccountForm>({
    mode: 'onBlur',
    defaultValues: {
      displayName: '',
      email: emailOverload || null,
      walletAddress,
      hcaptchaToken: null,
      // Honey pot fields, should not be filled out
      firstName: '',
      lastName: ''
    }
  })

  useEffect(() => {
    if (previousWalletAddress !== undefined && previousWalletAddress !== walletAddress) {
      // currently we are just logging out users if they try to switch accounts
      logout()
    }
  }, [previousWalletAddress, walletAddress])

  const onSelectProfilePicture = (profilePictureFile: File) => {
    setProfilePicture(profilePictureFile)
  }

  const onClickSubmit: SubmitHandler<CreateAccountForm> = async (data) => {
    createUser.mutate(
      { ...data, profilePicture: profilePicture ?? undefined, hcaptchaToken },
      {
        onSuccess: async (user) => {
          segmentIdentifyUser(user)
          await silentLogin()

          setFlowPosition('verifyOnTwitter')
          setIsLoggedIn(true)

          const walletProvider = getWalletProviderCookie()
          segmentSignUp(walletAddress, user.email, user.displayName, walletProvider)
        }
      }
    )
  }

  return (
    <Flex direction={'column'} css={{ justifyContent: 'center', alignItems: 'center' }}>
      <Text css={{ textAlign: 'center' }} size={'32'} type={'primary'}>
        Create an account
      </Text>

      <HoneyPotFields
        honeyPotFields={{ firstName: '', lastName: '' }}
        handleHoneyPotChange={({ firstName, lastName }) => {
          setValue('firstName', firstName)
          setValue('lastName', lastName)
        }}
      />

      <Spacer type={'vertical_4'} />

      <SelectProfilePicture onChange={onSelectProfilePicture} profilePicture={profilePicture} />

      <Spacer type={'vertical_2'} />

      <form style={{ width: '100%' }}>
        <Flex direction={'column'} width={'full'}>
          <FieldLabel htmlFor="displayName">Display name*</FieldLabel>
          <Controller
            name="displayName"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              minLength: {
                value: 4,
                message: 'At least 4 characters'
              },
              maxLength: {
                value: 30,
                message: 'At most 30 characters'
              },
              validate: {
                uniqueName: async (newDisplayName) =>
                  (await getIsValidDisplayName(newDisplayName)) ||
                  'This display name is already taken'
              }
            }}
            render={({ field }) => (
              <TextInput id="displayName" placeholder="Your display name" {...field} />
            )}
          />
          <FieldErrorLabel>{errors.displayName?.message}</FieldErrorLabel>
        </Flex>

        <Flex
          direction={'column'}
          width={'full'}
          css={{
            marginRight: '16px'
          }}
        >
          <FieldLabel htmlFor="email">Email*</FieldLabel>
          {emailOverload === null || emailOverload === undefined ? (
            <Controller
              name="email"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Required'
                },
                pattern: {
                  value: emailRegex,
                  message: 'Enter a valid email address'
                }
              }}
              render={({ field }) => (
                <TextInput id="email" placeholder="Your email address" {...field} />
              )}
            />
          ) : (
            <TextInput id="email" value={emailOverload} disabled={true} />
          )}
          <FieldErrorLabel>{errors.email?.message}</FieldErrorLabel>
        </Flex>

        <Flex direction={'column'} width={'full'}>
          <FieldLabel htmlFor="walletAddress">Wallet Address</FieldLabel>
          <Controller
            name="walletAddress"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              minLength: {
                value: 2,
                message: 'At least 2 characters'
              }
            }}
            render={({ field }) => (
              <TextInput
                disabled={true}
                id="walletAddress"
                placeholder="Your wallet address"
                {...field}
              />
            )}
          />
          <FieldErrorLabel>{errors.walletAddress?.message}</FieldErrorLabel>
        </Flex>
        <HCaptcha
          sitekey="2a5462be-4e99-4e95-9e22-baddd11e84a3"
          onOpen={() => {
            // Hacky fix for Hcaptcha not working nicely with Radix Dialog.
            // More details: https://github.com/radix-ui/primitives/issues/1385
            var hcaptchaElement = document.querySelector(
              '[title="Main content of the hCaptcha challenge"]'
            )
            if (hcaptchaElement) {
              ;(hcaptchaElement as HTMLElement).style.pointerEvents = 'auto'
            } else {
              console.warn("Warning, hcaptcha element doesn't exist, can't be fixed.")
            }
          }}
          onVerify={(token, ekey) => {
            setHcaptchaToken(token)
          }}
        />
      </form>

      <Spacer type={'vertical_5'} />

      <Flex direction={'column'} width="full">
        <Button
          type={'dark'}
          big={true}
          rounded={true}
          disabled={createUser.isLoading || !isValid || !hcaptchaToken}
          onClick={handleSubmit(onClickSubmit)}
        >
          {createUser.isLoading ? (
            <Spinner />
          ) : (
            <Text type={'primary_inverted'} size={'14'}>
              Create Account
            </Text>
          )}
        </Button>
      </Flex>

      <Spacer type={'vertical_5'} />

      <Flex css={{ display: 'inline-flex', maxWidth: '400px' }}>
        <Text css={{ textAlign: 'center' }} size={'14'} type={'secondary'}>
          {'By clicking "Create Account", you state the you have read and agreed to '}
          <a
            referrerPolicy="no-referrer"
            target={'_blank'}
            style={{ textDecoration: 'underline' }}
            href={'https://beta.arpeggi.io/legal/terms-of-service'}
          >
            Arpeggi's Terms of Service
          </a>
          {' and '}
          <a
            referrerPolicy="no-referrer"
            target={'_blank'}
            style={{ textDecoration: 'underline' }}
            href={'https://www.arpeggi.io/legal/privacy-policy'}
          >
            Privacy Policy
          </a>
        </Text>
      </Flex>
    </Flex>
  )
}

export default CreateAccount
